// React
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

// Form seam (GRAIN-2551)
import { useFormContext } from '@components/Form/FormProvider';

// Types
import type { CreateEventFormEvent } from '@/react/schemas/createEvent.schema';

const SCROLL_OFFSET_PX = 16;

export function createEmptyEvent(id: number): CreateEventFormEvent {
  return {
    id,
    form: '',
    productLine: '',
    paxCount: 0,
    lines: 0,
    ways: 0,
    tags: [],
    scheduledAt: undefined,
    servingTime: '',
    description: '',
    specialInstructions: '',
    expanded: true,
  };
}

// Event-list actions shared between the form body and the footer's Add button;
// list mutations go through the form store, highlight/scroll stay transient UI.
interface CreateEventActions {
  highlightedEventId: number | null;
  addEvent: () => void;
  duplicateEvent: (index: number) => void;
  deleteEvent: (index: number) => void;
  toggleExpanded: (index: number) => void;
}

const CreateEventActionsContext = createContext<CreateEventActions | null>(null);

export function useCreateEventActions(): CreateEventActions {
  const ctx = useContext(CreateEventActionsContext);
  if (!ctx) throw new Error('CreateEvent components must be used within CreateEventActionsProvider');
  return ctx;
}

interface CreateEventActionsProviderProps {
  scrollRef?: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

export function CreateEventActionsProvider({ scrollRef, children }: CreateEventActionsProviderProps) {
  const { form } = useFormContext();
  const [highlightedEventId, setHighlightedEventId] = useState<number | null>(null);

  const getEvents = useCallback(
    () => ((form.getFieldValue('events' as never) as CreateEventFormEvent[] | undefined) ?? []),
    [form],
  );
  const setEvents = useCallback(
    (next: CreateEventFormEvent[]) => form.setFieldValue('events' as never, next as never),
    [form],
  );

  const initialised = useRef(false);
  const nextEventId = useRef(1);
  if (!initialised.current) {
    const events = getEvents();
    nextEventId.current = events.length ? Math.max(...events.map((e) => e.id)) + 1 : 1;
    initialised.current = true;
  }

  const scrollToEvent = useCallback(
    (id: number) => {
      requestAnimationFrame(() => {
        const container = scrollRef?.current;
        const eventElement = container?.querySelector<HTMLElement>(`[data-event-id="${id}"]`);
        if (!container || !eventElement) return;
        const elementTop =
          eventElement.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
        container.scrollTo({ top: elementTop - SCROLL_OFFSET_PX, behavior: 'smooth' });
      });
    },
    [scrollRef],
  );

  const addEvent = useCallback(() => {
    const id = nextEventId.current++;
    setEvents([...getEvents().map((e) => ({ ...e, expanded: false })), createEmptyEvent(id)]);
    setHighlightedEventId(id);
    scrollToEvent(id);
  }, [getEvents, setEvents, scrollToEvent]);

  const duplicateEvent = useCallback(
    (index: number) => {
      const events = getEvents();
      const source = events[index];
      if (!source) return;
      const id = nextEventId.current++;
      setEvents([...events.map((e) => ({ ...e, expanded: false })), { ...source, id, expanded: true }]);
      setHighlightedEventId(id);
      scrollToEvent(id);
    },
    [getEvents, setEvents, scrollToEvent],
  );

  const deleteEvent = useCallback(
    (index: number) => {
      const remaining = getEvents().filter((_, i) => i !== index);
      setEvents(remaining.length === 0 ? [createEmptyEvent(nextEventId.current++)] : remaining);
    },
    [getEvents, setEvents],
  );

  const toggleExpanded = useCallback(
    (index: number) => {
      const events = getEvents();
      const target = events[index];
      if (target && highlightedEventId === target.id) setHighlightedEventId(null);
      setEvents(events.map((e, i) => (i === index ? { ...e, expanded: !e.expanded } : e)));
    },
    [getEvents, setEvents, highlightedEventId],
  );

  return (
    <CreateEventActionsContext.Provider
      value={{ highlightedEventId, addEvent, duplicateEvent, deleteEvent, toggleExpanded }}
    >
      {children}
    </CreateEventActionsContext.Provider>
  );
}
